import { resolveCartShippingRow, type AttachedShipping } from "./attached-shipping";
import {
	resolveFreeShippingProgress,
	resolveShippingFlatRate,
	type FreeShippingProgressState,
} from "@/checkout/views/saleor-checkout/free-shipping-core";

export type SummaryFreshness = "ready" | "updating" | "unavailable";
export type DeliveryDisplayState = {
	status: "unresolved" | "confirmed" | "updating" | "failed" | "unavailable";
	amountIsKnown: boolean;
	appliesToCurrentCart: boolean;
	shippingRequired: boolean;
};
export type CommerceSummaryInput = {
	scope: "selection" | "checkout";
	channel: string;
	currency: string;
	subtotalAmount: number;
	discountAmount: number;
	resolvedTotalAmount: number;
	freeShippingBasisAmount: number;
	attached: AttachedShipping;
	/** Gross component of the API total; checkout shipping rows may use a net amount. */
	attachedShippingGrossAmount?: number;
	/** Independently known merchandise gross amount, with the voucher already accounted for. */
	merchandiseGrossAmount?: number;
	delivery: DeliveryDisplayState;
	freshness: SummaryFreshness;
};
export type CommerceSummary = {
	scope: "selection" | "checkout";
	channel: string;
	currency: string;
	subtotalAmount: number;
	discountAmount: number;
	totalAmount: number | null;
	totalKind: "estimated" | "confirmed" | "subtotal-only" | "unavailable";
	shipping: {
		kind:
			| "estimated-paid"
			| "estimated-free"
			| "confirmed-paid"
			| "confirmed-free"
			| "not-required"
			| "updating"
			| "failed"
			| "unavailable";
		amount: number | null;
	};
	freeShipping: FreeShippingProgressState & {
		message: "remaining" | "estimated-qualified" | "confirmed-free" | "hidden";
	};
	freshness: SummaryFreshness;
};

const validAmount = (amount: number) => Number.isFinite(amount) && amount >= 0;
const roundMoney = (amount: number) => Math.round((amount + Number.EPSILON) * 100) / 100;

/** Display-only: callers must never use the estimated total to authorize a payment. */
export function resolveCommerceSummary(input: CommerceSummaryInput): CommerceSummary {
	const progress = resolveFreeShippingProgress(input.freeShippingBasisAmount, input.channel);
	const result: CommerceSummary = {
		scope: input.scope,
		channel: input.channel,
		currency: input.currency,
		subtotalAmount: input.subtotalAmount,
		discountAmount: input.discountAmount,
		totalAmount: null,
		totalKind: "subtotal-only",
		shipping: { kind: "unavailable", amount: null },
		freeShipping: { ...progress, message: "hidden" },
		freshness: input.freshness,
	};
	if (
		![
			input.subtotalAmount,
			input.discountAmount,
			input.resolvedTotalAmount,
			input.freeShippingBasisAmount,
			input.attached.amount,
			input.attachedShippingGrossAmount ?? 0,
			input.merchandiseGrossAmount ?? 0,
		].every(validAmount)
	) {
		return { ...result, totalKind: "unavailable", freshness: "unavailable" };
	}
	if (input.freshness !== "ready") return { ...result, shipping: { kind: input.freshness, amount: null } };
	if (!input.delivery.shippingRequired)
		return {
			...result,
			totalAmount: roundMoney(input.resolvedTotalAmount),
			totalKind: "confirmed",
			shipping: { kind: "not-required", amount: 0 },
		};
	// Delivery edits invalidate the next quote, not the gross components of this
	// existing checkout snapshot. Never substitute its net merchandise subtotal.
	const merchandiseGrossAmount =
		input.attachedShippingGrossAmount !== undefined
			? input.resolvedTotalAmount - input.attachedShippingGrossAmount
			: input.merchandiseGrossAmount !== undefined
				? input.merchandiseGrossAmount
				: !input.attached.usesAttachedShipping
					? input.resolvedTotalAmount
					: input.delivery.amountIsKnown
						? input.resolvedTotalAmount - input.attached.amount
						: null;
	result.totalAmount =
		merchandiseGrossAmount === null ? null : roundMoney(Math.max(0, merchandiseGrossAmount));
	result.freeShipping.message = progress.qualified ? "estimated-qualified" : "remaining";
	if (["updating", "failed", "unavailable"].includes(input.delivery.status)) {
		return {
			...result,
			shipping: { kind: input.delivery.status as "updating" | "failed" | "unavailable", amount: null },
		};
	}
	if (input.attached.usesAttachedShipping || input.delivery.status === "confirmed") {
		if (
			!input.attached.usesAttachedShipping ||
			input.delivery.status !== "confirmed" ||
			!input.delivery.amountIsKnown ||
			!input.delivery.appliesToCurrentCart
		)
			return result;
	}
	const row = resolveCartShippingRow({
		attached: input.attached,
		resolvedTotal: input.resolvedTotalAmount,
		freeShippingQualified: progress.qualified,
		flatRateAmount: resolveShippingFlatRate(input.channel),
	});
	const confirmed = input.attached.usesAttachedShipping;
	return {
		...result,
		totalAmount: roundMoney(row.grandTotal),
		totalKind: confirmed ? "confirmed" : "estimated",
		shipping: {
			kind: confirmed
				? row.isFree
					? "confirmed-free"
					: "confirmed-paid"
				: row.isFree
					? "estimated-free"
					: "estimated-paid",
			amount: roundMoney(row.shippingAmount),
		},
		freeShipping: {
			...progress,
			message: confirmed
				? row.isFree
					? "confirmed-free"
					: progress.qualified
						? "hidden"
						: "remaining"
				: progress.qualified
					? "estimated-qualified"
					: "remaining",
		},
	};
}
